import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  Toolbar,
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HomeIcon from '@mui/icons-material/Home';
import ChatIcon from '@mui/icons-material/Chat';
import LogoutIcon from '@mui/icons-material/Logout';
import DisplaySettingsIcon from '@mui/icons-material/DisplaySettings';
import SettingsIcon from '@mui/icons-material/Settings';
import MonitorIcon from '@mui/icons-material/Monitor';
import SelfImprovementIcon from '@mui/icons-material/SelfImprovement';

import { auth } from './firebase';
import logo from './vica.png';

const drawerWidth = 250;

// App-wide navigation rail, rendered once in AppShell at the far left of the
// screen (left of the avatar). The compact rail shows icons; the chevron opens
// a temporary drawer with labels.
function NavRail() {
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Logout asks first, so a stray tap doesn't sign the caregiver out.
  const [confirmLogoutOpen, setConfirmLogoutOpen] = useState(false);

  const items = useMemo(
    () => [
      { label: 'Home', icon: <HomeIcon />, route: '/home' },
      { label: 'Care Sessions', icon: <ChatIcon />, route: '/sessions' },
      { label: 'HABC Monitor', icon: <MonitorIcon />, route: '/habc-monitor' },
      { label: 'Respite Record', icon: <SelfImprovementIcon />, route: '/respite-record' },
      { label: 'Environment', icon: <DisplaySettingsIcon />, route: '/environment' },
      { label: 'Profile Settings', icon: <SettingsIcon />, route: '/profile-settings' },
      { label: 'Logout', icon: <LogoutIcon />, route: null },
    ],
    []
  );

  const handleItemClick = (item) => {
    setDrawerOpen(false);
    if (item.route) {
      navigate(item.route);
      return;
    }
    setConfirmLogoutOpen(true);
  };

  const handleConfirmLogout = async () => {
    setConfirmLogoutOpen(false);
    await auth.signOut();
    localStorage.removeItem('userId');
    localStorage.removeItem('sessionId');
    localStorage.removeItem('sessionIsNew');
    localStorage.removeItem('firebaseUid');
    localStorage.removeItem('authEmail');
    navigate('/', { replace: true });
  };

  return (
    <>
      <nav className="vica-nav-rail" aria-label="Main navigation" data-tour="nav">
        <IconButton
          onClick={() => setDrawerOpen(true)}
          sx={{ color: '#e9f5fc' }}
          title="Open menu"
          className="vica-nav-rail-toggle"
        >
          <ChevronRightIcon sx={{ fontSize: 30 }} />
        </IconButton>

        <List className="vica-nav-rail-list" sx={{ px: 0.5, py: 0 }}>
          {items.map((item) => {
            const isActive = item.route && location.pathname === item.route;
            return (
              <ListItem key={item.label} disablePadding sx={{ mb: 0.8, justifyContent: 'center' }}>
                <ListItemButton
                  onClick={() => handleItemClick(item)}
                  sx={{
                    minHeight: 58,
                    width: 58,
                    borderRadius: '14px',
                    px: 0,
                    justifyContent: 'center',
                    backgroundColor: isActive ? 'rgba(255,255,255,0.22)' : 'transparent',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.16)' },
                  }}
                  title={item.label}
                >
                  <ListItemIcon sx={{ color: '#d9eef9', minWidth: 0, '& svg': { fontSize: 32 } }}>
                    {item.icon}
                  </ListItemIcon>
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </nav>

      <Drawer
        variant="temporary"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: drawerWidth,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            overflowX: 'hidden',
            paddingTop: 'max(10px, env(safe-area-inset-top))',
            paddingBottom: 'max(10px, env(safe-area-inset-bottom))',
            border: 'none',
            background: 'linear-gradient(195deg, #034b71 0%, #0a6aa0 100%)',
            color: '#e9f5fc',
            boxShadow: '8px 0 25px rgba(0, 43, 65, 0.2)',
          },
        }}
      >
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <img src={logo} alt="VICA" className="h-9 w-auto" />
          <IconButton onClick={() => setDrawerOpen(false)} sx={{ color: '#e9f5fc' }}>
            <ChevronLeftIcon />
          </IconButton>
        </Toolbar>

        <List sx={{ px: 1 }}>
          {items.map((item) => {
            const isActive = item.route && location.pathname === item.route;
            return (
              <ListItem key={item.label} disablePadding sx={{ mb: 0.8 }}>
                <ListItemButton
                  onClick={() => handleItemClick(item)}
                  sx={{
                    minHeight: 46,
                    borderRadius: '12px',
                    px: 1.4,
                    backgroundColor: isActive ? 'rgba(255,255,255,0.22)' : 'transparent',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.16)' },
                  }}
                >
                  <ListItemIcon sx={{ color: '#d9eef9', minWidth: 34 }}>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.label} />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Drawer>

      <Dialog
        open={confirmLogoutOpen}
        onClose={() => setConfirmLogoutOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: '20px', p: 1 } }}
      >
        <DialogTitle sx={{ fontSize: '2rem', fontWeight: 800, color: '#0f3950', pb: 1 }}>
          Log out?
        </DialogTitle>
        <DialogContent sx={{ fontSize: '1.4rem', lineHeight: 1.6, color: '#1c3f52' }}>
          Are you sure you want to log out of VICA? You will need your email and password to sign in again.
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1.5 }}>
          <button
            className="vica-btn-secondary vica-session-btn-new justify-center"
            onClick={() => setConfirmLogoutOpen(false)}
            autoFocus
          >
            Cancel
          </button>
          <button className="vica-btn vica-session-btn-new justify-center" onClick={handleConfirmLogout}>
            <LogoutIcon sx={{ fontSize: 28 }} />
            Yes, Log Out
          </button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default NavRail;
